import { PermissionCode } from "../../permissions/permissions.js";
import {
  BSCAN_IMAGE_KIND_OPTIONS,
  BSCAN_LAUDO_OPTIONS,
  BSCAN_LEGEND,
  BSCAN_PHOTO_KIND_OPTIONS,
  BSCAN_SCAN_MODE_OPTIONS,
  applicableLimitMm,
  areaOf,
  bscanLaudoLabel,
  formatExtent,
  formatMm,
  nextBscanItem,
  removeScanArea,
  suggestBscanLaudo,
  summarizeBscan,
  type BscanArea,
  type BscanImage,
  type BscanLaudo,
  type BscanPhoto,
  type BscanReport
} from "../../domain/ndt/bscan-report-domain.js";
import { parseDecimalBR } from "../../domain/engineering/me-measurements.js";
import { formatCivilDateBR } from "../../domain/ndt/lp-consumable.js";
import { COUPLANT_OTHER, formatCatalogItemLabel, type NdtCatalogItem } from "../../domain/ndt/ndt-technical-catalog.js";
import { formatTransducerDimension, formatTransducerFrequency } from "../../domain/ndt/ndt-transducer.js";
import { calibrationStatusLabel, formatCalibrationBlockRange, resolveCalibrationStatus } from "../../domain/ndt/ndt-calibration.js";
import { isEmbeddableImage } from "../../domain/ndt/ndt-report-common.js";
import { formatFileSize } from "./lp-report-context.js";
import { NdtReportPageBase, type NdtReportPageConfig } from "./ndt-report-page-base.js";
import { operationalSettings } from "../settings/operational-settings.js";

/** Campos numéricos (mm) — gravados como número, digitados com vírgula. */
const REPORT_NUMERIC_FIELDS = new Set(["nominalThicknessMm", "minDesignThicknessMm"]);
const AREA_NUMERIC_FIELDS = new Set(["lengthXMm", "lengthYMm", "nominalThicknessMm", "minDesignThicknessMm"]);
const IMAGE_NUMERIC_FIELDS = new Set(["associatedThicknessMm"]);

const newKey = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/** Espessura do cadastro pode vir número, texto ou MeasurementValue ({ value }). */
function thicknessOf(raw: any): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "object") return parseDecimalBR(raw.value ?? null);
  return parseDecimalBR(raw);
}

/**
 * RELATÓRIO DE MEDIÇÃO DE ESPESSURA POR B-SCAN / C-SCAN — página de elaboração.
 *
 * Construída sobre a base de END: contexto, hidratação, autosave, signatários,
 * card ANÁLISE DO PLH e snapshot vêm da base — não existe fluxo paralelo.
 * As áreas de varredura são a espinha: resultados, fotos, imagens e marcadores
 * do isométrico apontam para a área por `scanAreaKey`, e remover uma área usa
 * `removeScanArea` (domínio), que decide o que cai junto.
 */
class AppBscanReportPage extends NdtReportPageBase<BscanReport> {
  protected readonly config: NdtReportPageConfig = {
    method: "BSCAN",
    documentType: "BSCAN",
    title: "Relatório de Medição de Espessura por B-Scan / C-Scan",
    subtitle: "Mapeamento de espessura por varredura ultrassônica — áreas, resultados, isométrico e imagens.",
    routeBase: "/ndt/bscan",
    reportLabel: "Relatório B-Scan / C-Scan",
    serviceKey: "bscanReportService",
    permissions: {
      view: PermissionCode.BscanReportView, create: PermissionCode.BscanReportCreate, edit: PermissionCode.BscanReportEdit, validate: PermissionCode.BscanReportValidate,
      generatePdf: PermissionCode.BscanReportGeneratePdf, approve: PermissionCode.BscanReportApprove, sign: PermissionCode.BscanReportSign, manageSignatories: PermissionCode.BscanReportManageSignatories
    },
    sections: [["01", "Identificação"], ["02", "Objetivo"], ["03", "Dados Técnicos"], ["04", "Aparelho e Calibração"], ["05", "Critério de Aceitação"], ["06", "Áreas de Varredura"], ["07", "Resultados"], ["08", "Isométrico / Croqui"], ["09", "Fotografias"], ["10", "Imagens B-Scan"], ["11", "Observações"], ["12", "Signatários"], ["13", "Resumo"]],
    itemPrefix: "BS",
    plhReview: true
  };

  private devices: any[] = [];
  private transducerAssets: any[] = [];
  private calibrationBlocks: any[] = [];
  private couplants: NdtCatalogItem[] = [];
  private couplantOtherOpen = false;
  /** Área escolhida para marcar no isométrico — o clique na imagem cria o marcador dela. */
  private markerAreaKey = "";

  protected override seedExtras(equipment: any) {
    return { component: equipment?.name || equipment?.description || "" };
  }

  protected async loadSpecific(): Promise<void> {
    const r = this.report;
    const ctx = this.scopedCtx(r?.clientId);
    const assets = window.app.platform.ndtAssetService;
    const keepLinked = (list: any[], linkedIds: (string | null | undefined)[]) => list.filter(a => a.status !== "INACTIVE" || linkedIds.includes(a.id));
    const [couplants, devices, transducers, blocks] = await Promise.all([
      window.app.platform.ndtCatalogService.listSelectableForReport(ctx, "COUPLANT", "BSCAN", r?.couplantId ?? null).catch(() => []),
      assets.listAssets(ctx, "ULTRASONIC_THICKNESS_GAUGE", true).catch(() => []),
      assets.listAssets(ctx, "ULTRASONIC_TRANSDUCER", true).catch(() => []),
      assets.listAssets(ctx, "CALIBRATION_BLOCK", true).catch(() => [])
    ]);
    this.couplants = couplants;
    this.devices = keepLinked(devices || [], [r?.deviceAssetId]);
    this.transducerAssets = keepLinked(transducers || [], [r?.transducerAssetId]);
    this.calibrationBlocks = keepLinked(blocks || [], [r?.calibrationBlockAssetId]);
    this.couplantOtherOpen = !!r && !r.couplantId && !!r.couplant;
    if (r) {
      this.prefillFromEquipment(r);
      await this.loadSignatureOf(r.authorizedInspector?.professionalId ?? null);
    }
  }

  private async loadSignatureOf(id: string | null) {
    if (!id || this.signatureImages.has(id)) return;
    try {
      const image = await window.app.platform.adminService.getProfessionalSignature(this.buildCtx(), id);
      this.signatureImages.set(id, isEmbeddableImage(image) ? image : null);
    } catch { this.signatureImages.set(id, null); }
  }

  private equipmentOf(r: BscanReport): any | null {
    return r.equipmentId ? this.equipments.find((e: any) => e.id === r.equipmentId) ?? null : null;
  }

  /**
   * SNAPSHOT TÉCNICO: só preenche o que está VAZIO no relatório. Um valor que a
   * pessoa já digitou nunca é sobrescrito pelo cadastro — o cadastro sugere, o
   * relatório registra o que foi efetivamente ensaiado.
   */
  private prefillFromEquipment(r: BscanReport) {
    if (r.status === "SIGNED" || r.status === "FINALIZED") return;
    const eq = this.equipmentOf(r);
    if (!eq) return;
    if (!r.nominalDiameter && eq.nominalDiameter) r.nominalDiameter = String(eq.nominalDiameter);
    if (r.nominalThicknessMm === null) r.nominalThicknessMm = thicknessOf(eq.nominalThickness);
    if (!r.baseMaterial) r.baseMaterial = eq.material || eq.materialSpecification || "";
    if (!r.drawingReference && (eq.isometricNumber || eq.drawingNumber)) r.drawingReference = String(eq.isometricNumber || eq.drawingNumber);
  }

  protected handleSpecificInput(target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, ds: DOMStringMap): boolean {
    const r = this.report;
    if (!r) return false;
    if (ds["numField"]) {
      if (REPORT_NUMERIC_FIELDS.has(ds["numField"]!)) (r as any)[ds["numField"]!] = parseDecimalBR(target.value);
      return true;
    }
    if (ds["areaKey"] && ds["areaField"]) {
      const area = r.scanAreas.find(a => a.key === ds["areaKey"]);
      if (area) {
        const field = ds["areaField"]!;
        (area as any)[field] = AREA_NUMERIC_FIELDS.has(field) ? parseDecimalBR(target.value) : target.value;
      }
      return true;
    }
    if (ds["resultKey"] && ds["resultField"]) {
      const row = r.results.find(x => x.key === ds["resultKey"]);
      if (row) {
        const field = ds["resultField"]!;
        if (field === "minimumThicknessMm") row.minimumThicknessMm = parseDecimalBR(target.value);
        else if (field === "laudo") row.laudo = target.value as BscanLaudo;
        else (row as any)[field] = target.value;
      }
      return true;
    }
    if (ds["bphotoKey"] && ds["bphotoField"]) {
      const photo = (r.photos as BscanPhoto[]).find(p => p.key === ds["bphotoKey"]);
      if (photo) {
        const field = ds["bphotoField"]!;
        if (field === "order") photo.order = Number(target.value) || 0;
        else if (field === "scanAreaKey") photo.scanAreaKey = target.value || null;
        else (photo as any)[field] = target.value;
      }
      return true;
    }
    if (ds["imageKey"] && ds["imageField"]) {
      const img = r.scanImages.find(i => i.key === ds["imageKey"]);
      if (img) {
        const field = ds["imageField"]!;
        if (IMAGE_NUMERIC_FIELDS.has(field)) (img as any)[field] = parseDecimalBR(target.value);
        else if (field === "order") img.order = Number(target.value) || 0;
        else if (field === "scanAreaKey") img.scanAreaKey = target.value || null;
        else (img as any)[field] = target.value;
      }
      return true;
    }
    if (ds["couplantOther"] !== undefined) {
      r.couplantId = null;
      r.couplant = target.value;
      return true;
    }
    return false;
  }

  // ------------------------------------------------------- SELECTIONS

  private onCouplantChange(itemId: string) {
    if (!this.report) return;
    if (itemId === COUPLANT_OTHER) {
      this.report.couplantId = null; this.report.couplant = ""; this.couplantOtherOpen = true;
    } else {
      const item = this.couplants.find(i => i.id === itemId);
      this.report.couplantId = itemId || null;
      this.report.couplant = item ? formatCatalogItemLabel(item) : "";
      this.couplantOtherOpen = false;
    }
    this.scheduleAutosave(); this.render();
  }

  private async onDeviceChange(assetId: string) {
    const r = this.report;
    if (!r) return;
    const device = this.devices.find(a => a.id === assetId);
    r.deviceAssetId = assetId || null;
    if (!device) {
      r.deviceManufacturer = ""; r.deviceModel = ""; r.deviceSerialNumber = ""; r.deviceCalibrationCert = ""; r.deviceCalibrationValidity = "";
    } else {
      r.deviceManufacturer = device.manufacturer || "";
      r.deviceModel = device.model || "";
      r.deviceSerialNumber = device.serialNumber || "";
      const cert = await this.latestCertificateOf(device.id);
      r.deviceCalibrationCert = cert?.certificateNumber || "";
      r.deviceCalibrationValidity = cert?.expirationDate || "";
    }
    this.scheduleAutosave(); this.render();
  }

  private onTransducerChange(assetId: string) {
    const r = this.report;
    if (!r) return;
    const asset = this.transducerAssets.find(a => a.id === assetId);
    r.transducerAssetId = assetId || null;
    if (!asset) {
      r.transducerManufacturer = ""; r.transducerModel = ""; r.transducerFrequency = ""; r.transducerAngle = ""; r.transducerSerialNumber = ""; r.transducerDimension = "";
    } else {
      const td = asset.technicalData || {};
      r.transducerManufacturer = asset.manufacturer || "";
      r.transducerModel = asset.model || "";
      r.transducerFrequency = formatTransducerFrequency(td.frequencyMHz);
      r.transducerAngle = td.nominalAngle !== undefined && td.nominalAngle !== null ? `${td.nominalAngle}°` : "";
      r.transducerSerialNumber = asset.serialNumber || "";
      r.transducerDimension = formatTransducerDimension(td.elementDiameter, td.dimensionUnit) || td.elementDimensions || "";
    }
    this.scheduleAutosave(); this.render();
  }

  private async onBlockChange(assetId: string) {
    const r = this.report;
    if (!r) return;
    const block = this.calibrationBlocks.find(a => a.id === assetId);
    r.calibrationBlockAssetId = assetId || null;
    if (!block) {
      r.calibrationBlock = ""; r.calibrationBlockCert = ""; r.calibrationBlockCertValidity = "";
    } else {
      r.calibrationBlock = [block.internalCode, block.serialNumber, block.technicalData?.material, formatCalibrationBlockRange(block)].filter(Boolean).join(" · ");
      const cert = await this.latestCertificateOf(block.id);
      r.calibrationBlockCert = cert?.certificateNumber || "";
      r.calibrationBlockCertValidity = cert?.expirationDate || "";
    }
    this.scheduleAutosave(); this.render();
  }

  private addArea() {
    const r = this.report;
    if (!r) return;
    r.scanAreas.push({ key: newKey("BSCAN-AREA"), item: nextBscanItem(r.scanAreas.map(a => a.item)), code: "", identification: "", scanFile: "", location: "", lengthXMm: null, lengthYMm: null, orientation: "", nominalThicknessMm: null, minDesignThicknessMm: null, observation: "" });
    this.scheduleAutosave(); this.render();
  }

  private removeArea(key: string) {
    const r = this.report;
    if (!r) return;
    const area = areaOf(r, key);
    const linked = r.results.filter(x => x.scanAreaKey === key).length + r.scanImages.filter(i => i.scanAreaKey === key).length + r.drawingMarkers.filter(m => m.scanAreaKey === key).length;
    if (linked > 0 && !confirm(`Remover a área ${area?.code || area?.item || ""}? ${linked} registro(s) vinculado(s) — resultados, imagens e marcadores — serão removidos junto. As fotografias ficam, apenas desvinculadas.`)) return;
    Object.assign(r, removeScanArea(r, key));
    if (this.markerAreaKey === key) this.markerAreaKey = "";
    this.scheduleAutosave(); this.render();
  }

  private addResult() {
    const r = this.report;
    if (!r) return;
    const used = new Set(r.results.map(x => x.scanAreaKey));
    const firstFree = r.scanAreas.find(a => !used.has(a.key)) ?? r.scanAreas[0];
    r.results.push({ key: newKey("BSCAN-RES"), scanAreaKey: firstFree?.key ?? "", item: nextBscanItem(r.results.map(x => x.item)), minimumThicknessMm: null, laudo: "", observation: "" });
    this.scheduleAutosave(); this.render();
  }

  private removeResult(key: string) {
    if (!this.report) return;
    this.report.results = this.report.results.filter(x => x.key !== key);
    this.scheduleAutosave(); this.render();
  }

  /** A base grava a foto só com os campos comuns; aqui ela ganha tipo, área, legenda e ordem. */
  protected override async onPhotoFiles(files: FileList | File[]) {
    await super.onPhotoFiles(files);
    const r = this.report;
    if (!r) return;
    r.photos = (r.photos as BscanPhoto[]).map((p, i) => ({ ...p, scanAreaKey: p.scanAreaKey ?? null, kind: p.kind || "LOCAL_VARREDURA", caption: p.caption ?? "", order: p.order || i + 1 }));
    this.render();
  }

  private async onScanImageFiles(files: FileList | File[]) {
    const r = this.report;
    if (!r) return;
    for (const file of Array.from(files)) {
      const rejected = this.rejectFile(file);
      if (rejected) { this.notify(rejected, "error"); continue; }
      const image = await this.readFileAsDataUrl(file);
      if (!isEmbeddableImage(image)) { this.notify(`"${file.name}": formato não suportado no documento.`, "error"); continue; }
      const img: BscanImage = { key: newKey("BSCAN-IMG"), scanAreaKey: r.scanAreas[0]?.key ?? null, kind: "B_SCAN", image, fileName: file.name, fileSize: file.size, uploadedAt: new Date().toISOString(), caption: "", associatedThicknessMm: null, observation: "", order: r.scanImages.length + 1 };
      r.scanImages.push(img);
    }
    this.scheduleAutosave(); this.render();
  }

  private removeScanImage(key: string) {
    if (!this.report) return;
    this.report.scanImages = this.report.scanImages.filter(i => i.key !== key);
    this.scheduleAutosave(); this.render();
  }

  private placeMarker(e: MouseEvent, box: HTMLElement) {
    const r = this.report;
    if (!r) return;
    if (!this.markerAreaKey) { this.notify("Escolha a área de varredura antes de marcar no isométrico.", "info"); return; }
    const rect = box.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    const x = clamp((e.clientX - rect.left) / rect.width);
    const y = clamp((e.clientY - rect.top) / rect.height);
    // Um marcador por área: marcar de novo reposiciona, não duplica.
    r.drawingMarkers = r.drawingMarkers.filter(m => m.scanAreaKey !== this.markerAreaKey);
    r.drawingMarkers.push({ key: newKey("BSCAN-MK"), scanAreaKey: this.markerAreaKey, x, y });
    this.scheduleAutosave(); this.render();
  }

  private removeMarker(key: string) {
    if (!this.report) return;
    this.report.drawingMarkers = this.report.drawingMarkers.filter(m => m.key !== key);
    this.scheduleAutosave(); this.render();
  }

  private async onAuthorizedInspectorChange(professionalId: string) {
    const r = this.report;
    if (!r) return;
    const prof = this.professionals.find((p: any) => p.id === professionalId);
    r.authorizedInspector = prof ? { role: "INSPETOR", professionalId: prof.id, name: prof.name || "", functionTitle: prof.functionTitle || "Inspetor Autorizado", credential: this.credentialFor(prof), signatureImageId: prof.signatureImageId || null, signedAt: null } : null;
    await this.loadSignatureOf(prof?.id ?? null);
    this.scheduleAutosave(); this.render();
  }

  // -------------------------------------------------------------- CARDS

  private num(value: number | null | undefined): string {
    return value === null || value === undefined ? "" : formatMm(value);
  }

  private areaLabel(a: BscanArea): string {
    return `${a.code || a.item}${a.identification ? " — " + a.identification : ""}`;
  }

  private areaOptions(r: BscanReport, current: string | null, emptyLabel: string): string {
    return this.opt("", emptyLabel, !current) + r.scanAreas.map(a => this.opt(a.key, this.areaLabel(a), current === a.key)).join("");
  }

  protected renderCards(r: BscanReport, ro: boolean, canSign: boolean): string {
    return [
      this.renderCardIdentification(r, ro),
      this.renderCardObjective(r, ro),
      this.renderCardTechnical(r, ro),
      this.renderCardEquipment(r, ro),
      this.renderCardAcceptance(r, ro),
      this.renderCardAreas(r, ro),
      this.renderCardResults(r, ro),
      this.renderCardDrawing(r, ro),
      this.renderCardBscanPhotos(r, ro),
      this.renderCardScanImages(r, ro),
      this.renderCardBscanObservations(r, ro),
      this.renderCardBscanSignatories(r, ro, canSign),
      this.renderCardSummary(r)
    ].join("");
  }

  private renderCardIdentification(r: BscanReport, ro: boolean): string {
    const eq = this.equipmentOf(r);
    const eqThickness = eq ? thicknessOf(eq.nominalThickness) : null;
    return `
      <section class="nr-card" id="nr-sec-01">
        <div class="card-head"><span class="card-title"><span class="card-num">01.</span> Identificação</span></div>
        <div class="grid cols-4">
          <label class="field"><span>Cliente</span><input class="ctl" readonly value="${this.esc(r.clientDisplayName || "—")}"></label>
          <label class="field"><span>Projeto / Contrato</span><input class="ctl" readonly value="${this.esc(r.projectContract || "—")}"></label>
          <label class="field"><span>Unidade / Área</span><input class="ctl" readonly value="${this.esc(r.unitArea || "—")}"></label>
          <label class="field"><span>Equipamento / TAG</span><input class="ctl" readonly value="${this.esc(r.equipmentTag || "—")}"></label>
        </div>
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Sistema</span><input class="ctl" data-field="system" value="${this.esc(r.system)}" placeholder="Sistema / circuito" ${this.dis(ro)}></label>
          <label class="field"><span>Descrição / Componente</span><input class="ctl" data-field="component" value="${this.esc(r.component)}" ${this.dis(ro)}></label>
          <label class="field"><span>Local da Inspeção</span><input class="ctl" data-field="inspectionLocation" value="${this.esc(r.inspectionLocation)}" placeholder="Campo / oficina" ${this.dis(ro)}></label>
          <label class="field"><span>Modalidade</span><input class="ctl" data-field="modality" value="${this.esc(r.modality)}" placeholder="Parada / em operação" ${this.dis(ro)}></label>
        </div>
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Material Base</span><input class="ctl" data-field="baseMaterial" value="${this.esc(r.baseMaterial)}" ${this.dis(ro)}></label>
          <label class="field"><span>Temperatura do Ensaio (°C)</span><input class="ctl" data-field="testTemperature" value="${this.esc(r.testTemperature)}" placeholder="25" ${this.dis(ro)}></label>
        </div>
        <div class="card-head" style="margin-top:14px;"><span class="card-title" style="font-size:0.76rem;">Snapshot técnico do ativo</span><span class="hint">Leitura do cadastro. Preenche só os campos vazios do relatório — o relatório registra o que foi ensaiado.</span></div>
        ${eq ? `<div class="asset-strip">
          <span><b>TAG</b>${this.esc(eq.tag || "—")}</span>
          <span><b>Material</b>${this.esc(eq.material || eq.materialSpecification || "NÃO INFORMADO")}</span>
          <span><b>Diâmetro nominal</b>${this.esc(eq.nominalDiameter ? String(eq.nominalDiameter) : "NÃO INFORMADO")}</span>
          <span><b>Espessura nominal</b>${this.esc(eqThickness !== null ? formatMm(eqThickness) + " mm" : "NÃO INFORMADO")}</span>
          <span><b>Isométrico</b>${this.esc(eq.isometricNumber || eq.drawingNumber || "NÃO INFORMADO")}</span>
        </div>` : `<p class="hint">${r.equipmentId ? "Equipamento não encontrado no cadastro do Cliente do documento." : "Selecione o Equipamento para ver o snapshot técnico."}</p>`}
      </section>`;
  }

  private renderCardObjective(r: BscanReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-02">
        <div class="card-head"><span class="card-title"><span class="card-num">02.</span> Objetivo</span></div>
        <textarea class="ctl" rows="3" data-field="objective" ${this.dis(ro)}>${this.esc(r.objective)}</textarea>
      </section>`;
  }

  private renderCardTechnical(r: BscanReport, ro: boolean): string {
    const couplantValue = r.couplantId ?? (this.couplantOtherOpen ? COUPLANT_OTHER : "");
    return `
      <section class="nr-card" id="nr-sec-03">
        <div class="card-head"><span class="card-title"><span class="card-num">03.</span> Procedimento, Norma e Dados Técnicos do Ensaio</span></div>
        ${this.renderProcedureFields(r, ro)}
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Modo de Varredura *</span><select class="ctl" data-field="scanMode" ${this.dis(ro)}>${this.opt("", "Selecione...", !r.scanMode)}${BSCAN_SCAN_MODE_OPTIONS.map(o => this.opt(o.value, o.label, r.scanMode === o.value)).join("")}</select></label>
          <label class="field"><span>Condição Superficial</span><input class="ctl" data-field="surfaceCondition" value="${this.esc(r.surfaceCondition)}" placeholder="Pintada / escovada" ${this.dis(ro)}></label>
          <label class="field"><span>Diâmetro Nominal</span><input class="ctl" data-field="nominalDiameter" value="${this.esc(r.nominalDiameter)}" placeholder='10"' ${this.dis(ro)}></label>
          <label class="field"><span>Espessura Nominal (mm)</span><input class="ctl" inputmode="decimal" data-num-field="nominalThicknessMm" value="${this.esc(this.num(r.nominalThicknessMm))}" placeholder="8,70" ${this.dis(ro)}></label>
        </div>
        <div class="grid cols-4" style="margin-top:12px;">
          <label class="field"><span>Técnica</span><input class="ctl" data-field="technique" value="${this.esc(r.technique)}" placeholder="Pulso-eco, contato" ${this.dis(ro)}></label>
          <label class="field"><span>Acoplante *</span>
            <select class="ctl" id="sel-couplant" ${this.dis(ro)}>${this.opt("", this.couplants.length === 0 ? "Nenhum acoplante cadastrado" : "Selecione...", !couplantValue)}${this.couplants.map(c => this.opt(c.id, formatCatalogItemLabel(c), couplantValue === c.id)).join("")}${this.opt(COUPLANT_OTHER, "Outro (informar)", couplantValue === COUPLANT_OTHER)}</select>
            ${this.couplantOtherOpen ? `<input class="ctl sm" data-couplant-other value="${this.esc(r.couplant)}" placeholder="Informe o acoplante" ${this.dis(ro)}>` : ""}</label>
          <label class="field"><span>Termômetro</span><input class="ctl" data-field="thermometer" value="${this.esc(r.thermometer)}" placeholder="Identificação / certificado" ${this.dis(ro)}></label>
          <label class="field"><span>Software de Análise</span><input class="ctl" data-field="analysisSoftware" value="${this.esc(r.analysisSoftware)}" ${this.dis(ro)}></label>
        </div>
        <div class="grid cols-2" style="margin-top:12px;">
          <label class="field"><span>Scanner / Encoder (fabricante)</span><input class="ctl" data-field="scannerManufacturer" value="${this.esc(r.scannerManufacturer)}" ${this.dis(ro)}></label>
          <label class="field"><span>Notas técnicas</span><input class="ctl" data-field="technicalNotes" value="${this.esc(r.technicalNotes)}" ${this.dis(ro)}></label>
        </div>
      </section>`;
  }

  private calStrip(validity: string, rows: Array<[string, string]>): string {
    const status = validity ? resolveCalibrationStatus(validity) : null;
    return `<div class="asset-strip">${rows.map(([k, v]) => `<span><b>${this.esc(k)}</b>${this.esc(v || "—")}</span>`).join("")}<span><b>Validade</b>${validity ? this.esc(formatCivilDateBR(validity.slice(0, 10))) : "—"}</span><span><b>Calibração</b><span class="${status === "EXPIRED" ? "warn" : ""}">${this.esc(calibrationStatusLabel(validity))}</span></span></div>`;
  }

  private renderCardEquipment(r: BscanReport, ro: boolean): string {
    const label = (a: any) => `${[a.internalCode, a.manufacturer, a.model].filter(Boolean).join(" · ")}${a.serialNumber ? " — S/N " + a.serialNumber : ""}`;
    return `
      <section class="nr-card" id="nr-sec-04">
        <div class="card-head"><span class="card-title"><span class="card-num">04.</span> Aparelho, Transdutor e Calibração</span></div>
        <div class="grid cols-3">
          <div>
            <label class="field"><span>Aparelho de Ultrassom *</span>
              <select class="ctl" id="sel-device" ${this.dis(ro)}>${this.opt("", this.devices.length === 0 ? "Nenhum aparelho cadastrado para este cliente" : "Selecione...", !r.deviceAssetId)}${this.devices.map(d => this.opt(d.id, label(d), r.deviceAssetId === d.id)).join("")}</select></label>
            ${r.deviceAssetId ? this.calStrip(r.deviceCalibrationValidity, [["Marca", r.deviceManufacturer], ["Modelo", r.deviceModel], ["Nº de Série", r.deviceSerialNumber], ["Certificado", r.deviceCalibrationCert || "NÃO INFORMADO"]]) : ""}
          </div>
          <div>
            <label class="field"><span>Transdutor *</span>
              <select class="ctl" id="sel-transducer" ${this.dis(ro)}>${this.opt("", this.transducerAssets.length === 0 ? "Nenhum transdutor cadastrado para este cliente" : "Selecione...", !r.transducerAssetId)}${this.transducerAssets.map(a => this.opt(a.id, label(a), r.transducerAssetId === a.id)).join("")}</select></label>
            ${r.transducerAssetId ? `<div class="asset-strip"><span><b>Frequência</b>${this.esc(r.transducerFrequency || "—")}</span><span><b>Ângulo</b>${this.esc(r.transducerAngle || "—")}</span><span><b>Dimensão</b>${this.esc(r.transducerDimension || "—")}</span><span><b>Nº de Série</b>${this.esc(r.transducerSerialNumber || "—")}</span></div>` : ""}
          </div>
          <div>
            <label class="field"><span>Bloco de Calibração *</span>
              <select class="ctl" id="sel-block" ${this.dis(ro)}>${this.opt("", this.calibrationBlocks.length === 0 ? "Nenhum bloco cadastrado para este cliente" : "Selecione...", !r.calibrationBlockAssetId)}${this.calibrationBlocks.map(b => this.opt(b.id, [b.internalCode, b.serialNumber].filter(Boolean).join(" / "), r.calibrationBlockAssetId === b.id)).join("")}</select></label>
            ${r.calibrationBlockAssetId ? this.calStrip(r.calibrationBlockCertValidity, [["Identificação", r.calibrationBlock], ["Certificado", r.calibrationBlockCert || "NÃO INFORMADO"]]) : ""}
          </div>
        </div>
      </section>`;
  }

  private renderCardAcceptance(r: BscanReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-05">
        <div class="card-head"><span class="card-title"><span class="card-num">05.</span> Critério de Aceitação</span><span class="hint">Norma e critério vêm do item 03. O limite abaixo vale para as áreas que não definem o seu.</span></div>
        <div class="grid cols-4">
          <label class="field"><span>Código de Aceitação</span><input class="ctl" data-field="acceptanceCode" value="${this.esc(r.acceptanceCode)}" placeholder="API 574" ${this.dis(ro)}></label>
          <label class="field"><span>Referência</span><input class="ctl" data-field="acceptanceReference" value="${this.esc(r.acceptanceReference)}" placeholder="Seção / tabela" ${this.dis(ro)}></label>
          <label class="field"><span>Espessura Mínima de Projeto (mm) *</span><input class="ctl" inputmode="decimal" data-num-field="minDesignThicknessMm" value="${this.esc(this.num(r.minDesignThicknessMm))}" placeholder="2,50" ${this.dis(ro)}></label>
          <label class="field"><span>Outro limite técnico</span><input class="ctl" data-field="otherTechnicalLimit" value="${this.esc(r.otherTechnicalLimit)}" ${this.dis(ro)}></label>
        </div>
      </section>`;
  }

  private areaCell(a: BscanArea, field: keyof BscanArea, ro: boolean, placeholder = "", numeric = false): string {
    const value = numeric ? this.num(a[field] as number | null) : String(a[field] ?? "");
    return `<input class="cell" ${numeric ? `inputmode="decimal"` : ""} data-area-key="${a.key}" data-area-field="${String(field)}" value="${this.esc(value)}" placeholder="${this.esc(placeholder)}" ${this.dis(ro)}>`;
  }

  private renderCardAreas(r: BscanReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-06">
        <div class="card-head"><span class="card-title"><span class="card-num">06.</span> Áreas de Varredura <span class="count">${r.scanAreas.length}</span></span>${!ro ? `<button class="btn sm primary" id="btn-add-area"><i data-lucide="plus"></i> Adicionar Área</button>` : ""}</div>
        <div class="table-wrap"><table class="nr-table">
          <thead><tr><th style="width:50px;">Item</th><th style="width:100px;">Código</th><th>Identificação</th><th style="width:110px;">Arquivo</th><th style="width:120px;">Localização</th><th style="width:70px;">X (mm)</th><th style="width:70px;">Y (mm)</th><th style="width:90px;">Orientação</th><th style="width:80px;">Esp. nom.</th><th style="width:80px;">Esp. mín. proj.</th><th style="width:50px;">Ações</th></tr></thead>
          <tbody>${r.scanAreas.length === 0 ? `<tr><td colspan="11" class="empty">Nenhuma área. Resultados, imagens e marcadores dependem de uma área — use "Adicionar Área".</td></tr>` : r.scanAreas.map(a => `
            <tr>
              <td>${this.areaCell(a, "item", ro)}</td><td>${this.areaCell(a, "code", ro, "BS09-61")}</td><td>${this.areaCell(a, "identification", ro, "Trecho reto")}</td><td>${this.areaCell(a, "scanFile", ro, "BS09-61.nde")}</td><td>${this.areaCell(a, "location", ro, "Geratriz inferior")}</td>
              <td>${this.areaCell(a, "lengthXMm", ro, "500", true)}</td><td>${this.areaCell(a, "lengthYMm", ro, "", true)}</td><td>${this.areaCell(a, "orientation", ro, "Sentido do fluxo")}</td>
              <td>${this.areaCell(a, "nominalThicknessMm", ro, this.num(r.nominalThicknessMm), true)}</td><td>${this.areaCell(a, "minDesignThicknessMm", ro, this.num(r.minDesignThicknessMm), true)}</td>
              <td style="text-align:center;">${!ro ? `<button class="icon-btn danger" data-remove-area="${a.key}" title="Remover área"><i data-lucide="trash-2"></i></button>` : ""}</td>
            </tr>`).join("")}</tbody>
        </table></div>
        <p class="hint" style="margin-top:8px;">Espessuras vazias na área herdam as do relatório (itens 03 e 05).${r.scanAreas.length > 0 ? ` Extensão: ${r.scanAreas.map(a => `${this.esc(a.code || a.item)} ${this.esc(formatExtent(a) || "—")}`).join(" · ")}` : ""}</p>
      </section>`;
  }

  private renderCardResults(r: BscanReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-07">
        <div class="card-head"><span class="card-title"><span class="card-num">07.</span> Resultados <span class="count">${r.results.length}</span></span>${!ro ? `<button class="btn sm primary" id="btn-add-result" ${r.scanAreas.length === 0 ? `disabled title="Cadastre uma área no item 06"` : ""}><i data-lucide="plus"></i> Adicionar Resultado</button>` : ""}</div>
        <div class="table-wrap"><table class="nr-table">
          <thead><tr><th style="width:50px;">Item</th><th style="width:200px;">Área de Varredura</th><th style="width:110px;">Esp. mínima (mm)</th><th style="width:90px;">Limite (mm)</th><th style="width:220px;">Laudo</th><th>Observação</th><th style="width:50px;">Ações</th></tr></thead>
          <tbody>${r.results.length === 0 ? `<tr><td colspan="7" class="empty">Nenhum resultado registrado.</td></tr>` : r.results.map(row => {
            const area = areaOf(r, row.scanAreaKey);
            const limit = applicableLimitMm(r, area);
            const hint = suggestBscanLaudo(row.minimumThicknessMm, limit);
            const diverges = !!row.laudo && !!hint.suggestion && hint.suggestion !== row.laudo;
            return `
            <tr>
              <td><input class="cell" data-result-key="${row.key}" data-result-field="item" value="${this.esc(row.item)}" ${this.dis(ro)}></td>
              <td><select class="cell" data-result-key="${row.key}" data-result-field="scanAreaKey" data-rerender ${this.dis(ro)}>${this.areaOptions(r, row.scanAreaKey || null, "Selecione a área...")}</select></td>
              <td><input class="cell" inputmode="decimal" data-result-key="${row.key}" data-result-field="minimumThicknessMm" data-rerender value="${this.esc(this.num(row.minimumThicknessMm))}" placeholder="8,10" ${this.dis(ro)}></td>
              <td>${limit === null ? "—" : this.esc(formatMm(limit))}</td>
              <td><select class="cell" data-result-key="${row.key}" data-result-field="laudo" data-rerender ${this.dis(ro)}>${this.opt("", "Selecione...", !row.laudo)}${BSCAN_LAUDO_OPTIONS.map(o => this.opt(o.value, `${o.abbreviation} — ${o.label}`, row.laudo === o.value)).join("")}</select>
                ${hint.suggestion ? `<small class="hint ${diverges ? "warn" : ""}" title="${this.esc(hint.reason)}">Sugestão: ${this.esc(bscanLaudoLabel(hint.suggestion))}${diverges ? " — difere do laudo escolhido" : ""}</small>` : ""}</td>
              <td><input class="cell" data-result-key="${row.key}" data-result-field="observation" value="${this.esc(row.observation)}" ${this.dis(ro)}></td>
              <td style="text-align:center;">${!ro ? `<button class="icon-btn danger" data-remove-result="${row.key}" title="Remover"><i data-lucide="trash-2"></i></button>` : ""}</td>
            </tr>`;
          }).join("")}</tbody>
        </table></div>
        <p class="hint" style="margin-top:8px;">${this.esc(BSCAN_LEGEND)}. A sugestão compara a espessura mínima com o limite aplicável — o laudo é sempre decisão do inspetor.</p>
      </section>`;
  }

  private renderCardDrawing(r: BscanReport, ro: boolean): string {
    const c = r.croqui;
    const markers = r.drawingMarkers.map(m => ({ m, area: areaOf(r, m.scanAreaKey) }));
    const tag = (area: BscanArea | null) => this.esc(area ? area.code || area.item : "Área removida");
    return `
      <section class="nr-card" id="nr-sec-08">
        <div class="card-head"><span class="card-title"><span class="card-num">08.</span> Isométrico / Croqui</span>${!ro ? `<button class="btn sm primary" id="btn-croqui-attach"><i data-lucide="upload"></i> ${c ? "Substituir" : "Anexar"} Isométrico</button>` : ""}</div>
        <div class="grid cols-3">
          <label class="field"><span>Referência do desenho</span><input class="ctl" data-field="drawingReference" value="${this.esc(r.drawingReference)}" placeholder="ISO-U33-0012" ${this.dis(ro)}></label>
          <label class="field"><span>Revisão</span><input class="ctl" data-field="drawingRevision" value="${this.esc(r.drawingRevision)}" ${this.dis(ro)}></label>
          <label class="field"><span>Descrição</span><input class="ctl" data-field="drawingDescription" value="${this.esc(r.drawingDescription)}" ${this.dis(ro)}></label>
        </div>
        ${c ? `
          ${!ro ? `<div class="grid cols-3" style="margin-top:12px;"><label class="field"><span>Marcar área no desenho</span><select class="ctl" id="sel-marker-area">${this.areaOptions(r, this.markerAreaKey || null, r.scanAreas.length === 0 ? "Cadastre áreas no item 06" : "Escolha a área e clique no desenho...")}</select></label></div>` : ""}
          <div id="bscan-drawing" style="position:relative; display:inline-block; max-width:100%; margin-top:12px; cursor:${!ro && this.markerAreaKey ? "crosshair" : "default"};">
            <img src="${c.image}" alt="Isométrico" style="display:block; max-width:100%; max-height:520px;" draggable="false">
            ${markers.map(({ m, area }) => `<span style="position:absolute; left:${(m.x * 100).toFixed(2)}%; top:${(m.y * 100).toFixed(2)}%; transform:translate(-50%,-50%); background:#8a5a1f; color:#fff; font-size:11px; font-weight:700; padding:2px 6px; border-radius:10px; pointer-events:none; white-space:nowrap;">${tag(area)}</span>`).join("")}
          </div>
          <div class="file-meta" style="margin-top:6px;">${this.esc(c.fileName)} · ${this.esc(formatFileSize(c.fileSize))}${!ro ? ` <button class="btn sm danger" id="btn-croqui-remove"><i data-lucide="trash-2"></i> Remover</button>` : ""}</div>
          ${markers.length > 0 ? `<div class="asset-strip" style="margin-top:8px;">${markers.map(({ m, area }) => `<span><b>${tag(area)}</b>${(m.x * 100).toFixed(0)}% · ${(m.y * 100).toFixed(0)}%${!ro ? ` <button class="icon-btn danger" data-remove-marker="${m.key}" title="Remover marcador">✕</button>` : ""}</span>`).join("")}</div>` : ""}`
        : `<label class="dropzone" for="inp-croqui" style="margin-top:12px;"><i data-lucide="image"></i><span>Arraste o isométrico aqui ou clique para selecionar</span><small>PNG, JPG ou WEBP · até ${formatFileSize(operationalSettings().files.maxImageMb * 1024 * 1024)}</small></label>`}
        <input type="file" id="inp-croqui" accept="image/png,image/jpeg,image/webp" style="display:none;" ${this.dis(ro)}>
      </section>`;
  }

  private renderCardBscanPhotos(r: BscanReport, ro: boolean): string {
    const photos = r.photos as BscanPhoto[];
    return `
      <section class="nr-card" id="nr-sec-09">
        <div class="card-head"><span class="card-title"><span class="card-num">09.</span> Registros Fotográficos <span class="count">${photos.length}</span></span>${!ro ? `<button class="btn sm primary" id="btn-photo-add"><i data-lucide="plus"></i> Adicionar Foto</button>` : ""}</div>
        ${photos.length === 0 ? `<label class="dropzone" for="inp-photos"><i data-lucide="image"></i><span>Arraste as fotografias aqui ou clique para selecionar</span><small>PNG, JPG ou WEBP · até ${formatFileSize(operationalSettings().files.maxImageMb * 1024 * 1024)} cada</small></label>` : `
          <div class="photo-grid">${photos.map((p, i) => `
            <div class="photo-card">
              <img class="thumb" src="${p.image}" alt="${this.esc(p.fileName)}" data-lightbox="${this.esc(p.caption || p.fileName)}">
              <div class="photo-body">
                <div class="file-name">Foto ${String(i + 1).padStart(2, "0")} · ${this.esc(p.fileName)}</div>
                <select class="ctl sm" data-bphoto-key="${p.key}" data-bphoto-field="kind" ${this.dis(ro)}>${BSCAN_PHOTO_KIND_OPTIONS.map(o => this.opt(o.value, o.label, p.kind === o.value)).join("")}</select>
                <select class="ctl sm" data-bphoto-key="${p.key}" data-bphoto-field="scanAreaKey" ${this.dis(ro)}>${this.areaOptions(r, p.scanAreaKey, "Registro geral (sem área)")}</select>
                <input class="ctl sm" data-bphoto-key="${p.key}" data-bphoto-field="caption" value="${this.esc(p.caption)}" placeholder="Legenda" ${this.dis(ro)}>
                <input class="ctl sm" type="number" min="1" data-bphoto-key="${p.key}" data-bphoto-field="order" value="${p.order || i + 1}" title="Ordem no documento" ${this.dis(ro)}>
                <div class="attachment-actions"><button class="btn sm" data-view-photo="${p.key}"><i data-lucide="eye"></i></button>${!ro ? `<button class="btn sm danger" data-remove-photo="${p.key}"><i data-lucide="trash-2"></i></button>` : ""}</div>
              </div>
            </div>`).join("")}</div>`}
        <input type="file" id="inp-photos" accept="image/png,image/jpeg,image/webp" multiple style="display:none;" ${this.dis(ro)}>
      </section>`;
  }

  private renderCardScanImages(r: BscanReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-10">
        <div class="card-head"><span class="card-title"><span class="card-num">10.</span> Imagens B-Scan / C-Scan <span class="count">${r.scanImages.length}</span></span>${!ro ? `<button class="btn sm primary" id="btn-image-add"><i data-lucide="plus"></i> Adicionar Imagem</button>` : ""}</div>
        ${r.scanImages.length === 0 ? `<label class="dropzone" for="inp-scan-images"><i data-lucide="image"></i><span>Arraste as imagens do software aqui ou clique para selecionar</span><small>Cada imagem sai em bloco próprio, na largura total · PNG ou JPG</small></label>` : `
          <div class="photo-grid">${r.scanImages.map((img, i) => `
            <div class="photo-card">
              <img class="thumb" src="${img.image}" alt="${this.esc(img.fileName)}" data-lightbox="${this.esc(img.caption || img.fileName)}">
              <div class="photo-body">
                <div class="file-name">Imagem ${String(i + 1).padStart(2, "0")} · ${this.esc(img.fileName)}</div>
                <select class="ctl sm" data-image-key="${img.key}" data-image-field="kind" ${this.dis(ro)}>${BSCAN_IMAGE_KIND_OPTIONS.map(o => this.opt(o.value, o.label, img.kind === o.value)).join("")}</select>
                <select class="ctl sm" data-image-key="${img.key}" data-image-field="scanAreaKey" ${this.dis(ro)}>${this.areaOptions(r, img.scanAreaKey, "Sem área vinculada")}</select>
                <input class="ctl sm" data-image-key="${img.key}" data-image-field="caption" value="${this.esc(img.caption)}" placeholder="Legenda (vazia = automática)" ${this.dis(ro)}>
                <input class="ctl sm" inputmode="decimal" data-image-key="${img.key}" data-image-field="associatedThicknessMm" value="${this.esc(this.num(img.associatedThicknessMm))}" placeholder="Espessura associada (mm)" ${this.dis(ro)}>
                <input class="ctl sm" data-image-key="${img.key}" data-image-field="observation" value="${this.esc(img.observation)}" placeholder="Observação" ${this.dis(ro)}>
                <input class="ctl sm" type="number" min="1" data-image-key="${img.key}" data-image-field="order" value="${img.order || i + 1}" title="Ordem no documento" ${this.dis(ro)}>
                ${!ro ? `<div class="attachment-actions"><button class="btn sm danger" data-remove-image="${img.key}"><i data-lucide="trash-2"></i></button></div>` : ""}
              </div>
            </div>`).join("")}</div>`}
        <input type="file" id="inp-scan-images" accept="image/png,image/jpeg" multiple style="display:none;" ${this.dis(ro)}>
      </section>`;
  }

  private renderCardBscanObservations(r: BscanReport, ro: boolean): string {
    return `
      <section class="nr-card" id="nr-sec-11">
        <div class="card-head"><span class="card-title"><span class="card-num">11.</span> Observações</span></div>
        <div class="grid cols-2">
          <label class="field"><span>Observações gerais</span><textarea class="ctl" rows="4" data-field="observations" ${this.dis(ro)}>${this.esc(r.observations || "")}</textarea></label>
          <label class="field"><span>Observações técnicas</span><textarea class="ctl" rows="4" data-field="technicalObservations" ${this.dis(ro)}>${this.esc(r.technicalObservations)}</textarea></label>
        </div>
      </section>`;
  }

  private renderCardBscanSignatories(r: BscanReport, ro: boolean, canSign: boolean): string {
    const ai = r.authorizedInspector;
    const canChoose = !ro && r.status !== "SIGNED" && this.canManageSignatories(r);
    const image = ai?.professionalId ? this.signatureImages.get(ai.professionalId) : null;
    const actives = this.professionals.filter((p: any) => p.status === "ACTIVE" || p.id === ai?.professionalId);
    return `
      <section class="nr-card" id="nr-sec-12">
        <div class="card-head"><span class="card-title"><span class="card-num">12.</span> Termo de Responsabilidade & Signatários</span></div>
        <div class="sig-grid">
          ${this.renderSignatoryCard(r, "INSPETOR", ro, canSign)}
          ${this.renderSignatoryCard(r, "PLH", ro, canSign)}
          <div class="sig-card ${canChoose ? "" : "readonly"}">
            <div class="sig-role">Inspetor Autorizado</div>
            <div class="sig-image">${image ? `<img src="${image}" alt="Assinatura">` : `<span class="sig-blank">${ai?.professionalId ? "sem assinatura cadastrada" : "—"}</span>`}</div>
            ${canChoose ? `<select class="ctl sm" id="sel-authorized-inspector">${this.opt("", "Selecione (opcional)...", !ai?.professionalId)}${actives.map((p: any) => this.opt(p.id, `${p.name}${p.functionTitle ? " — " + p.functionTitle : ""}`, ai?.professionalId === p.id)).join("")}</select>` : `<div class="sig-locked">${ai?.professionalId ? "definido pela Engenharia" : "não designado"}</div>`}
            <div class="sig-name">${this.esc(ai?.name || "—")}</div>
            <div class="sig-meta">${this.esc(ai?.credential || "")}</div>
          </div>
          ${this.renderSignatoryCard(r, "FISCAL", ro, canSign)}
        </div>
        <p class="hint" style="margin-top:10px;">O PLH aprova pelo fluxo central (ANÁLISE DO PLH). As assinaturas são as imagens cadastradas em cada Profissional; o Cliente assina no documento impresso.</p>
      </section>`;
  }

  private renderCardSummary(r: BscanReport): string {
    const s = summarizeBscan(r);
    const c = s.laudoCounts;
    return `
      <section class="nr-card" id="nr-sec-13">
        <div class="card-head"><span class="card-title"><span class="card-num">13.</span> Resumo</span></div>
        <div class="asset-strip">
          <span><b>Áreas</b>${s.areaCount}</span><span><b>Resultados</b>${s.resultCount}</span><span><b>Fotos</b>${s.photoCount}</span><span><b>Imagens</b>${s.imageCount}</span>
          <span><b>Menor espessura</b>${s.globalMinimum ? `${this.esc(formatMm(s.globalMinimum.thicknessMm))} mm (${this.esc(s.globalMinimum.areaCode)})` : "—"}</span>
          <span><b>Laudos</b>A ${c.APROVADO} · R ${c.REPROVADO} · REC ${c.EXAME_COMPLEMENTAR} · ADPC ${c.ADPC}${s.pendingLaudo > 0 ? ` · <span class="warn">${s.pendingLaudo} sem laudo</span>` : ""}</span>
          <span><b>Norma</b>${this.esc(s.norm || "—")}</span><span><b>Critério</b>${this.esc(s.criteria || "—")}</span>
          <span><b>Signatários</b>${this.esc(s.signatories.join(", ") || "—")}</span>
        </div>
        ${s.pendencies.length > 0 ? `<div class="hint" style="margin-top:10px;"><b>Pendências para liberar ao PLH:</b><ul style="margin:4px 0 0 18px;">${s.pendencies.map(p => `<li>${this.esc(p)}</li>`).join("")}</ul></div>` : `<p class="hint" style="margin-top:10px;">Sem pendências de preenchimento.</p>`}
      </section>`;
  }

  // ------------------------------------------------------------- EVENTS

  protected attachSpecificEvents(ro: boolean): void {
    if (ro) return;
    this.querySelector("#sel-couplant")?.addEventListener("change", e => this.onCouplantChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-device")?.addEventListener("change", e => this.onDeviceChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-transducer")?.addEventListener("change", e => this.onTransducerChange((e.target as HTMLSelectElement).value));
    this.querySelector("#sel-block")?.addEventListener("change", e => this.onBlockChange((e.target as HTMLSelectElement).value));
    this.querySelector("#btn-add-area")?.addEventListener("click", () => this.addArea());
    this.querySelectorAll("[data-remove-area]").forEach(b => b.addEventListener("click", () => this.removeArea((b as HTMLElement).dataset["removeArea"]!)));
    this.querySelector("#btn-add-result")?.addEventListener("click", () => this.addResult());
    this.querySelectorAll("[data-remove-result]").forEach(b => b.addEventListener("click", () => this.removeResult((b as HTMLElement).dataset["removeResult"]!)));
    // Limite e sugestão dependem de área/espessura: redesenha no "change" (ao sair do campo), nunca a cada tecla — não rouba o foco.
    this.querySelectorAll("[data-rerender], [data-area-field]").forEach(el => el.addEventListener("change", () => this.render()));
    this.querySelector("#sel-marker-area")?.addEventListener("change", e => { this.markerAreaKey = (e.target as HTMLSelectElement).value; this.render(); });
    const drawing = this.querySelector("#bscan-drawing") as HTMLElement | null;
    drawing?.addEventListener("click", e => this.placeMarker(e as MouseEvent, drawing));
    this.querySelectorAll("[data-remove-marker]").forEach(b => b.addEventListener("click", () => this.removeMarker((b as HTMLElement).dataset["removeMarker"]!)));
    const imgInput = this.querySelector("#inp-scan-images") as HTMLInputElement | null;
    this.querySelector("#btn-image-add")?.addEventListener("click", () => imgInput?.click());
    imgInput?.addEventListener("change", () => { if (imgInput.files && imgInput.files.length > 0) this.onScanImageFiles(imgInput.files); });
    this.bindDropzone("label[for='inp-scan-images']", files => this.onScanImageFiles(files));
    this.querySelectorAll("[data-remove-image]").forEach(b => b.addEventListener("click", () => this.removeScanImage((b as HTMLElement).dataset["removeImage"]!)));
    this.querySelector("#sel-authorized-inspector")?.addEventListener("change", e => this.onAuthorizedInspectorChange((e.target as HTMLSelectElement).value));
  }
}

if (!customElements.get("app-bscan-report-page")) {
  customElements.define("app-bscan-report-page", AppBscanReportPage);
}
